<template>
  <div class="p-6 transition-all duration-300 min-h-screen bg-gray-50 dark:bg-gray-950">

    <!-- Header Section -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
      <div class="space-y-1">
        <h1 class="text-3xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400">
          Productos y Servicios
        </h1>
        <p class="text-gray-500 dark:text-gray-400 text-sm font-medium">
          Gestión unificada de ítems para el sistema
        </p>
      </div>

      <button @click="abrirModalCrear" class="btn-primary group flex items-center gap-2">
        <Icon icon="mdi:plus-circle" class="text-xl group-hover:rotate-90 transition-transform duration-300" />
        <span class="font-bold">Nuevo Registro</span>
      </button>
    </div>

    <!-- Stats Summary -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 text-white">
      <div class="p-6 rounded-3xl bg-gradient-to-br from-purple-500/90 to-pink-600/90 shadow-xl border border-white/10 backdrop-blur-sm">
        <div class="flex items-center gap-4">
          <div class="p-3 bg-white/20 rounded-2xl flex-shrink-0">
            <Icon icon="mdi:package-variant-closed" class="text-2xl" />
          </div>
          <div>
            <p class="text-purple-100 text-xs font-semibold uppercase tracking-wider">Total Registros</p>
            <p class="text-3xl font-bold">{{ items.length }}</p>
          </div>
        </div>
      </div>
      <div class="p-6 rounded-3xl bg-gradient-to-br from-emerald-500/90 to-teal-600/90 shadow-xl border border-white/10 backdrop-blur-sm">
        <div class="flex items-center gap-4">
          <div class="p-3 bg-white/20 rounded-2xl flex-shrink-0">
            <Icon icon="mdi:check-circle" class="text-2xl" />
          </div>
          <div>
            <p class="text-emerald-100 text-xs font-semibold uppercase tracking-wider">Activos</p>
            <p class="text-3xl font-bold">{{ items.filter(i => i.estado === 'activo').length }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters & Search -->
    <div class="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 mb-8">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
        <div class="relative group">
          <label class="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Buscar</label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl group-focus-within:text-purple-500 transition-colors pointer-events-none" />
            <input
              v-model="filtros.busqueda"
              type="text"
              placeholder="Código o nombre..."
              class="input-premium pl-12"
              @input="paginaActual = 1"
            >
          </div>
        </div>

        <div>
          <label class="block text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Ordenar Por</label>
          <select v-model="filtros.orden" @change="paginaActual = 1" class="input-premium">
            <option value="nombre">Nombre (A-Z)</option>
            <option value="nombre_desc">Nombre (Z-A)</option>
            <option value="codigo">Código</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Table Section -->
    <div class="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full border-collapse min-w-[600px]">
          <thead>
            <tr class="bg-gray-50/80 dark:bg-gray-800/60 text-gray-400 dark:text-gray-500 text-xs font-bold uppercase tracking-widest border-b border-gray-100 dark:border-gray-800">
              <th class="px-6 py-4 text-left w-28">Código</th>
              <th class="px-6 py-4 text-left">Información</th>
              <th class="px-6 py-4 text-center w-28">Estado</th>
              <th class="px-6 py-4 text-center w-32">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-800/50">

            <!-- Loading -->
            <tr v-if="cargando">
              <td colspan="4" class="px-6 py-20 text-center">
                <Icon icon="mdi:loading" class="text-5xl text-purple-500 animate-spin mx-auto mb-3" />
                <p class="text-gray-400 font-medium">Cargando registros...</p>
              </td>
            </tr>

            <!-- Empty -->
            <tr v-else-if="itemsFiltrados.length === 0">
              <td colspan="4" class="px-6 py-20 text-center">
                <Icon icon="mdi:database-off" class="text-5xl text-gray-300 mx-auto mb-3" />
                <p class="text-gray-400 font-medium">No se encontraron resultados</p>
              </td>
            </tr>

            <!-- Rows -->
            <tr
              v-else
              v-for="item in itemsPaginados"
              :key="item.idProductoServicio"
              class="hover:bg-purple-50/30 dark:hover:bg-purple-500/5 transition-colors"
            >
              <!-- Código -->
              <td class="px-6 py-4">
                <span class="inline-block px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg text-xs font-bold border border-gray-200 dark:border-gray-700 whitespace-nowrap">
                  {{ item.codigo }}
                </span>
              </td>

              <!-- Información -->
              <td class="px-6 py-4">
                <div class="flex flex-col gap-0.5">
                  <span class="text-gray-800 dark:text-gray-200 font-semibold text-sm leading-tight">{{ item.nombre }}</span>
                  <span class="text-gray-400 dark:text-gray-500 text-xs line-clamp-1 max-w-xs">{{ item.descripcion || 'Sin descripción' }}</span>
                </div>
              </td>

              <!-- Estado -->
              <td class="px-6 py-4 text-center">
                <span
                  :class="[
                    'inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest',
                    item.estado === 'activo'
                      ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400'
                      : 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400'
                  ]"
                >
                  {{ item.estado }}
                </span>
              </td>

              <!-- Acciones -->
              <td class="px-6 py-4">
                <div class="flex justify-center items-center gap-2">
                  <button
                    @click="abrirModalEditar(item)"
                    class="p-2 rounded-xl bg-blue-500/10 text-blue-500 hover:bg-blue-500 hover:text-white transition-all shadow-sm"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil-outline" class="text-lg" />
                  </button>
                  <button
                    @click="confirmarEliminar(item)"
                    class="p-2 rounded-xl bg-rose-500/10 text-rose-600 hover:bg-rose-500 hover:text-white transition-all shadow-sm"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:trash-can-outline" class="text-lg" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPaginas > 1" class="flex flex-col sm:flex-row justify-between items-center px-8 py-5 bg-gray-50/50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-800 gap-4">
        <p class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
          Página <span class="text-purple-500">{{ paginaActual }}</span> de {{ totalPaginas }}
          &nbsp;·&nbsp;
          <span class="text-gray-400">{{ itemsFiltrados.length }} registros</span>
        </p>
        <div class="flex items-center gap-2">
          <button
            @click="paginaActual--"
            :disabled="paginaActual === 1"
            class="p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 disabled:opacity-30 hover:bg-purple-50 dark:hover:bg-gray-700 transition-all"
          >
            <Icon icon="mdi:chevron-left" class="text-xl" />
          </button>
          <button
            @click="paginaActual++"
            :disabled="paginaActual === totalPaginas"
            class="p-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 disabled:opacity-30 hover:bg-purple-50 dark:hover:bg-gray-700 transition-all"
          >
            <Icon icon="mdi:chevron-right" class="text-xl" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <BaseModal
      v-model="modalVisible"
      :title="editando ? 'Editar Registro' : 'Nuevo Registro'"
    >
      <form @submit.prevent="guardarItem" class="space-y-5">

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Código -->
          <div class="space-y-1.5">
            <label class="label-premium">Código <span class="text-rose-500">*</span></label>
            <input
              v-model="form.codigo"
              type="text"
              required
              placeholder="Ej: PS-001"
              class="input-premium"
            >
          </div>

          <!-- Estado -->
          <div class="space-y-1.5">
            <label class="label-premium">Estado</label>
            <select v-model="form.estado" class="input-premium">
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </div>
        </div>

        <!-- Nombre -->
        <div class="space-y-1.5">
          <label class="label-premium">Nombre <span class="text-rose-500">*</span></label>
          <input
            v-model="form.nombre"
            type="text"
            required
            placeholder="Nombre del producto o servicio"
            class="input-premium"
          >
        </div>

        <!-- Descripción -->
        <div class="space-y-1.5">
          <label class="label-premium">Descripción</label>
          <textarea
            v-model="form.descripcion"
            rows="3"
            placeholder="Detalles adicionales..."
            class="input-premium resize-none"
          ></textarea>
        </div>

        <!-- Botones -->
        <div class="flex justify-end gap-3 pt-2">
          <button
            type="button"
            @click="cerrarModal"
            class="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 font-bold hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
          >
            Cancelar
          </button>
          <button
            type="submit"
            :disabled="guardando"
            class="btn-primary px-8 flex items-center gap-2"
          >
            <Icon v-if="guardando" icon="mdi:loading" class="text-lg animate-spin" />
            <span>{{ guardando ? 'Guardando...' : (editando ? 'Guardar Cambios' : 'Registrar') }}</span>
          </button>
        </div>
      </form>
    </BaseModal>

  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { useToast } from 'vue-toastification';
import { useModalStore } from '@/store/modal';
import BaseModal from '@/components/common/BaseModal.vue';
import {
  obtenerProductoServicios,
  crearProductoServicio,
  actualizarProductoServicio,
  eliminarProductoServicio
} from '@/api/productoServicio.js';

export default {
  name: 'ProductoServicioList',
  components: { Icon, BaseModal },
  setup() {
    const toast = useToast();
    const modal = useModalStore();
    return { toast, modal };
  },
  data() {
    return {
      items: [],
      cargando: false,
      guardando: false,
      modalVisible: false,
      editando: false,
      paginaActual: 1,
      itemsPorPagina: 10,
      filtros: {
        busqueda: '',
        orden: 'nombre'
      },
      form: {
        idProductoServicio: null,
        codigo: '',
        nombre: '',
        descripcion: '',
        estado: 'activo'
      }
    };
  },
  computed: {
    itemsFiltrados() {
      let res = [...this.items];
      if (this.filtros.busqueda) {
        const q = this.filtros.busqueda.toLowerCase();
        res = res.filter(i =>
          i.nombre.toLowerCase().includes(q) ||
          i.codigo.toLowerCase().includes(q)
        );
      }
      if (this.filtros.orden === 'nombre') res.sort((a, b) => a.nombre.localeCompare(b.nombre));
      else if (this.filtros.orden === 'nombre_desc') res.sort((a, b) => b.nombre.localeCompare(a.nombre));
      else if (this.filtros.orden === 'codigo') res.sort((a, b) => a.codigo.localeCompare(b.codigo));
      return res;
    },
    itemsPaginados() {
      const idx = (this.paginaActual - 1) * this.itemsPorPagina;
      return this.itemsFiltrados.slice(idx, idx + this.itemsPorPagina);
    },
    totalPaginas() {
      return Math.ceil(this.itemsFiltrados.length / this.itemsPorPagina);
    }
  },
  mounted() {
    this.cargarItems();
  },
  methods: {
    async cargarItems() {
      this.cargando = true;
      try {
        const res = await obtenerProductoServicios();
        if (res.success) this.items = res.data;
        else this.toast.error('Error al cargar datos');
      } catch {
        this.toast.error('Error de conexión');
      } finally {
        this.cargando = false;
      }
    },
    abrirModalCrear() {
      this.editando = false;
      this.form = { idProductoServicio: null, codigo: '', nombre: '', descripcion: '', estado: 'activo' };
      this.modalVisible = true;
    },
    abrirModalEditar(item) {
      this.editando = true;
      this.form = { ...item };
      this.modalVisible = true;
    },
    cerrarModal() {
      this.modalVisible = false;
    },
    async guardarItem() {
      this.guardando = true;
      try {
        const op = this.editando
          ? actualizarProductoServicio(this.form.idProductoServicio, this.form)
          : crearProductoServicio(this.form);
        const res = await op;
        if (res.success) {
          this.toast.success(res.message);
          this.cerrarModal();
          this.cargarItems();
        } else {
          this.toast.error(res.error || 'Error en la operación');
        }
      } catch {
        this.toast.error('Error al procesar');
      } finally {
        this.guardando = false;
      }
    },
    async confirmarEliminar(item) {
      const confirmed = await this.modal.confirm({
        title: '¿Eliminar registro?',
        message: `¿Estás seguro de que deseas eliminar "${item.nombre}"? Esta acción no se puede deshacer.`,
        icon: 'mdi:trash-can-outline',
        iconType: 'danger',
        confirmText: 'Sí, eliminar',
        type: 'danger'
      });

      if (confirmed) {
        const del = await eliminarProductoServicio(item.idProductoServicio);
        if (del.success) {
          this.toast.success('Eliminado correctamente');
          this.cargarItems();
        } else {
          this.toast.error('No se pudo eliminar');
        }
      }
    }
  }
};
</script>

<style scoped>
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
